import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import {
  categories,
  subcategories,
  getMoviesByCategory,
} from '@/mocks/movies';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSubcategory, setActiveSubcategory] = useState<string>('全部');
  const [sortBy, setSortBy] = useState<string>('default');

  const category = categories.find((c) => c.slug === slug);
  const allMovies = useMemo(() => {
    if (!slug) return [];
    return getMoviesByCategory(slug);
  }, [slug]);

  const filteredMovies = useMemo(() => {
    let result = [...allMovies];
    if (activeSubcategory !== '全部') {
      result = result.filter((m) => m.subcategory === activeSubcategory);
    }
    if (sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'newest') {
      result.sort((a, b) => b.year - a.year);
    } else if (sortBy === 'popular') {
      result.sort((a, b) => b.views - a.views);
    }
    return result;
  }, [allMovies, activeSubcategory, sortBy]);

  const currentSubs = slug ? subcategories[slug] || [] : [];

  if (!category) {
    return (
      <div className="min-h-screen bg-background-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 flex items-center justify-center bg-background-800 rounded-full mx-auto mb-4">
            <i className="ri-error-warning-line text-3xl text-foreground-500"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-200 mb-2">分类未找到</h1>
          <Link
            to="/"
            className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-950">
        <Navbar />

        <main className="pt-16">
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          {/* Category Header */}
          <section className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-primary-500/15 rounded-xl">
                <i className={`${category.icon} text-2xl text-primary-400`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  共 {allMovies.length} 部{category.name}资源
                </p>
              </div>
            </div>

            {/* Category tabs */}
            <div className="flex items-center gap-2 flex-wrap mb-6">
              <button
                onClick={() => setActiveSubcategory('全部')}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                  activeSubcategory === '全部'
                    ? 'bg-primary-500 text-background-950'
                    : 'bg-background-800 text-foreground-400 hover:text-foreground-200'
                }`}
              >
                全部
              </button>
              {currentSubs.map((sub) => (
                <button
                  key={sub}
                  onClick={() => setActiveSubcategory(sub)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                    activeSubcategory === sub
                      ? 'bg-primary-500 text-background-950'
                      : 'bg-background-800 text-foreground-400 hover:text-foreground-200'
                  }`}
                >
                  {sub}
                </button>
              ))}
            </div>

            {/* Sort options */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-foreground-500">排序：</span>
              {[
                { key: 'default', label: '默认' },
                { key: 'popular', label: '最热' },
                { key: 'rating', label: '评分最高' },
                { key: 'newest', label: '最新' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap cursor-pointer transition-all ${
                    sortBy === opt.key
                      ? 'bg-secondary-500/20 text-secondary-300 border border-secondary-500/30'
                      : 'text-foreground-500 hover:text-foreground-300'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </section>

          {/* Movie Grid */}
          <section className="w-full px-4 md:px-6 pb-14">
            {filteredMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {filteredMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} variant="default" />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center bg-background-800 rounded-full mx-auto mb-4">
                  <i className="ri-movie-2-line text-2xl text-foreground-600"></i>
                </div>
                <p className="text-foreground-500">暂无相关内容</p>
              </div>
            )}
          </section>
        </main>

        <Footer />
      </div>

      {/* Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name} - 91电影视频`,
            description: `91电影视频${category.name}频道，提供海量免费${category.name}在线观看，高清不缓冲，每日更新。`,
            url: `https://91movie.com/category/${slug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '91电影视频',
              url: 'https://91movie.com',
            },
          }),
        }}
      />
    </>
  );
}